export type DocCategory = "Personal" | "Business" | "Employment" | "Real Estate" | "Legal";

export type DocType = {
  slug: string;
  name: string;
  emoji: string;
  category: DocCategory;
  shortDesc: string;
  hook?: string;
  related?: string[];
  hidden?: { title: string; desc: string; emoji: string }[];
  faqs?: { q: string; a: string }[];
};

const lease: DocType = {
  slug: "lease-agreement",
  name: "Lease Agreement",
  emoji: "🏠",
  category: "Real Estate",
  shortDesc: "Understand your rental contract before signing",
  hook: "Don't sign until you know what's in it. We flag unfair clauses, hidden fees, and unusual terms.",
  related: ["rental-application", "sublease-agreement", "move-in-checklist"],
  hidden: [
    {
      emoji: "🔁",
      title: "Automatic Renewal Clauses",
      desc: "Your lease may auto-renew under the same terms — sometimes at a higher rent — without your explicit consent. We flag the renewal trigger, notice window, and any rent escalation built in.",
    },
    {
      emoji: "🛠️",
      title: "Hidden Maintenance Fees",
      desc: "Some leases shift repair costs to tenants that are legally the landlord's responsibility in your state. We highlight clauses that push appliance, plumbing, or HVAC costs onto you.",
    },
    {
      emoji: "🚪",
      title: "Early Termination Penalties",
      desc: "Penalties for breaking a lease early may exceed what state law allows. We compare your clause against typical caps so you know what's negotiable.",
    },
    {
      emoji: "💰",
      title: "Security Deposit Conditions",
      desc: "We surface unreasonable deductions, missing return timelines, and conditions that make it harder to get your deposit back.",
    },
    {
      emoji: "🔑",
      title: "Subletting & Guest Restrictions",
      desc: "Limits on your right to sublet, host guests, or assign the lease can be overly restrictive — and sometimes unenforceable.",
    },
  ],
  faqs: [
    {
      q: "Can legaldecoder review my lease agreement?",
      a: "Yes. Upload a PDF, paste the text, or snap a photo of a paper lease. Our AI returns red flags, rights and obligations, and suggested negotiation points in under 60 seconds.",
    },
    {
      q: "Is this legally binding advice?",
      a: "No. legaldecoder is an AI explainer. The analysis is for informational purposes only and is not a substitute for advice from a licensed attorney in your jurisdiction.",
    },
    {
      q: "What jurisdictions do you cover?",
      a: "We work on residential and commercial leases from any English-speaking jurisdiction. Risk flags reference common tenant-protection norms — always confirm specifics with a local attorney for high-stakes decisions.",
    },
    {
      q: "How long does the analysis take?",
      a: "Most leases are analyzed in 30–60 seconds. Long commercial leases with many exhibits may take up to two minutes.",
    },
    {
      q: "Do you store my lease?",
      a: "No. We use zero data retention — your document is processed in memory and discarded immediately. Nothing is stored or used to train AI models.",
    },
  ],
};

const nda: DocType = {
  slug: "nda",
  name: "NDA / Non-Disclosure Agreement",
  emoji: "📝",
  category: "Legal",
  shortDesc: "Know what you can and can't share",
  hook: "Before you sign, understand exactly what stays confidential, for how long, and what counts as a breach.",
  related: ["employment-contract", "service-agreement", "non-compete-agreement"],
  hidden: [
    {
      emoji: "⏳",
      title: "Indefinite Confidentiality Periods",
      desc: "Some NDAs bind you forever. We flag missing or unreasonable durations.",
    },
    {
      emoji: "🌐",
      title: "Overly Broad Scope",
      desc: "Vague definitions of 'confidential information' can cover things already public.",
    },
    {
      emoji: "⚖️",
      title: "Unfair Jurisdiction Clauses",
      desc: "Disputes may be forced into a far-off court chosen to favor the other side.",
    },
    {
      emoji: "💸",
      title: "Liquidated Damages",
      desc: "Pre-set penalties can far exceed actual harm caused by a breach.",
    },
    {
      emoji: "🚫",
      title: "Non-Solicit Side Effects",
      desc: "Hidden non-solicitation clauses can restrict who you may hire or contact.",
    },
  ],
};

const employment: DocType = {
  slug: "employment-contract",
  name: "Employment Contract",
  emoji: "💼",
  category: "Employment",
  shortDesc: "Review job terms, compensation, and clauses",
  related: ["non-compete-agreement", "severance-agreement", "nda"],
};

const service: DocType = {
  slug: "service-agreement",
  name: "Service Agreement",
  emoji: "📄",
  category: "Business",
  shortDesc: "Check freelancer and vendor contracts",
  related: ["nda", "independent-contractor-agreement"],
};

const tos: DocType = {
  slug: "terms-of-service",
  name: "Terms of Service",
  emoji: "📑",
  category: "Legal",
  shortDesc: "Decode website and app terms",
};

const termSheet: DocType = {
  slug: "investment-term-sheet",
  name: "Investment Term Sheet",
  emoji: "💰",
  category: "Business",
  shortDesc: "Review startup funding terms",
};

const partnership: DocType = {
  slug: "partnership-agreement",
  name: "Partnership Agreement",
  emoji: "🤝",
  category: "Business",
  shortDesc: "Understand business partnership terms",
};

const nonCompete: DocType = {
  slug: "non-compete-agreement",
  name: "Non-Compete Agreement",
  emoji: "🔒",
  category: "Employment",
  shortDesc: "Know your post-employment restrictions",
};

// Bulk additional types — concise entries for the SEO hub.
const more: DocType[] = [
  {
    slug: "rental-application",
    name: "Rental Application",
    emoji: "🔑",
    category: "Real Estate",
    shortDesc: "Spot risky background-check and fee clauses",
  },
  {
    slug: "sublease-agreement",
    name: "Sublease Agreement",
    emoji: "🏘️",
    category: "Real Estate",
    shortDesc: "Understand subletting rights and risks",
  },
  {
    slug: "month-to-month-lease",
    name: "Month-to-Month Lease",
    emoji: "📅",
    category: "Real Estate",
    shortDesc: "Catch surprise notice and rent-hike rules",
  },
  {
    slug: "commercial-lease",
    name: "Commercial Lease",
    emoji: "🏢",
    category: "Real Estate",
    shortDesc: "Decode CAM, NNN, and renewal terms",
  },
  {
    slug: "purchase-agreement",
    name: "Home Purchase Agreement",
    emoji: "🏡",
    category: "Real Estate",
    shortDesc: "Understand contingencies and disclosures",
  },
  {
    slug: "mortgage-agreement",
    name: "Mortgage Agreement",
    emoji: "🏦",
    category: "Real Estate",
    shortDesc: "Spot prepayment penalties and rate triggers",
  },
  {
    slug: "move-in-checklist",
    name: "Move-in Inspection Checklist",
    emoji: "📋",
    category: "Real Estate",
    shortDesc: "Protect your security deposit from day one",
  },
  {
    slug: "roommate-agreement",
    name: "Roommate Agreement",
    emoji: "👥",
    category: "Personal",
    shortDesc: "Set fair rules between roommates",
  },
  {
    slug: "prenuptial-agreement",
    name: "Prenuptial Agreement",
    emoji: "💍",
    category: "Personal",
    shortDesc: "Understand financial terms before marriage",
  },
  {
    slug: "postnuptial-agreement",
    name: "Postnuptial Agreement",
    emoji: "💞",
    category: "Personal",
    shortDesc: "Review post-marriage financial terms",
  },
  {
    slug: "divorce-settlement",
    name: "Divorce Settlement",
    emoji: "⚖️",
    category: "Personal",
    shortDesc: "Decode asset and custody terms",
  },
  {
    slug: "child-support-agreement",
    name: "Child Support Agreement",
    emoji: "👶",
    category: "Personal",
    shortDesc: "Understand payment and custody clauses",
  },
  {
    slug: "custody-agreement",
    name: "Custody Agreement",
    emoji: "🧒",
    category: "Personal",
    shortDesc: "Review parenting plans clearly",
  },
  {
    slug: "last-will-testament",
    name: "Last Will & Testament",
    emoji: "📜",
    category: "Personal",
    shortDesc: "Understand inheritance and executor terms",
  },
  {
    slug: "living-will",
    name: "Living Will",
    emoji: "🩺",
    category: "Personal",
    shortDesc: "Decode medical-directive language",
  },
  {
    slug: "power-of-attorney",
    name: "Power of Attorney",
    emoji: "🖊️",
    category: "Personal",
    shortDesc: "Understand who can act on your behalf",
  },
  {
    slug: "trust-agreement",
    name: "Trust Agreement",
    emoji: "🗝️",
    category: "Personal",
    shortDesc: "Review trustee and beneficiary terms",
  },
  {
    slug: "loan-agreement",
    name: "Personal Loan Agreement",
    emoji: "💵",
    category: "Personal",
    shortDesc: "Spot hidden interest and default clauses",
  },
  {
    slug: "promissory-note",
    name: "Promissory Note",
    emoji: "🧾",
    category: "Personal",
    shortDesc: "Understand repayment obligations",
  },
  {
    slug: "credit-card-agreement",
    name: "Credit Card Agreement",
    emoji: "💳",
    category: "Personal",
    shortDesc: "Decode APR, fees, and arbitration clauses",
  },
  {
    slug: "auto-loan-agreement",
    name: "Auto Loan Agreement",
    emoji: "🚗",
    category: "Personal",
    shortDesc: "Catch dealer add-ons and rate traps",
  },
  {
    slug: "auto-lease-agreement",
    name: "Auto Lease Agreement",
    emoji: "🚙",
    category: "Personal",
    shortDesc: "Understand mileage and wear-and-tear fees",
  },
  {
    slug: "insurance-policy",
    name: "Insurance Policy",
    emoji: "🛡️",
    category: "Personal",
    shortDesc: "Find exclusions and coverage caps",
  },
  {
    slug: "health-insurance-plan",
    name: "Health Insurance Plan",
    emoji: "🏥",
    category: "Personal",
    shortDesc: "Decode deductibles and network rules",
  },
  {
    slug: "gym-membership",
    name: "Gym Membership Agreement",
    emoji: "🏋️",
    category: "Personal",
    shortDesc: "Catch auto-renewals and cancel fees",
  },
  {
    slug: "subscription-contract",
    name: "Subscription Contract",
    emoji: "🔁",
    category: "Personal",
    shortDesc: "Spot price hikes and binding terms",
  },
  {
    slug: "online-service-agreement",
    name: "Online Service Agreement",
    emoji: "🌐",
    category: "Personal",
    shortDesc: "Understand SaaS and platform terms",
  },
  {
    slug: "privacy-policy",
    name: "Privacy Policy",
    emoji: "🔐",
    category: "Legal",
    shortDesc: "See exactly what data is collected",
  },
  {
    slug: "eula",
    name: "End-User License Agreement (EULA)",
    emoji: "💻",
    category: "Legal",
    shortDesc: "Know your software-use rights",
  },
  {
    slug: "cookie-policy",
    name: "Cookie Policy",
    emoji: "🍪",
    category: "Legal",
    shortDesc: "Understand tracking disclosures",
  },
  {
    slug: "data-processing-agreement",
    name: "Data Processing Agreement",
    emoji: "🗄️",
    category: "Legal",
    shortDesc: "Decode GDPR controller/processor terms",
  },
  {
    slug: "settlement-agreement",
    name: "Settlement Agreement",
    emoji: "🤝",
    category: "Legal",
    shortDesc: "Understand release and confidentiality clauses",
  },
  {
    slug: "release-of-liability",
    name: "Release of Liability / Waiver",
    emoji: "📝",
    category: "Legal",
    shortDesc: "Know what rights you're giving up",
  },
  {
    slug: "arbitration-agreement",
    name: "Arbitration Agreement",
    emoji: "⚖️",
    category: "Legal",
    shortDesc: "Understand forced arbitration clauses",
  },
  {
    slug: "court-order",
    name: "Court Order",
    emoji: "🏛️",
    category: "Legal",
    shortDesc: "Decode obligations in plain English",
  },
  {
    slug: "subpoena",
    name: "Subpoena",
    emoji: "📨",
    category: "Legal",
    shortDesc: "Understand what's being requested",
  },
  {
    slug: "cease-and-desist",
    name: "Cease & Desist Letter",
    emoji: "✋",
    category: "Legal",
    shortDesc: "Decode demands and your options",
  },
  {
    slug: "demand-letter",
    name: "Demand Letter",
    emoji: "✉️",
    category: "Legal",
    shortDesc: "Understand the claim and timeline",
  },
  {
    slug: "power-of-sale",
    name: "Power of Sale Agreement",
    emoji: "🔨",
    category: "Real Estate",
    shortDesc: "Decode foreclosure-related terms",
  },
  {
    slug: "deed-of-trust",
    name: "Deed of Trust",
    emoji: "📃",
    category: "Real Estate",
    shortDesc: "Understand lender rights and obligations",
  },
  {
    slug: "hoa-agreement",
    name: "HOA Agreement",
    emoji: "🏘️",
    category: "Real Estate",
    shortDesc: "Spot fees, rules, and assessments",
  },
  {
    slug: "property-management-agreement",
    name: "Property Management Agreement",
    emoji: "🏗️",
    category: "Real Estate",
    shortDesc: "Review manager fees and duties",
  },
  {
    slug: "construction-contract",
    name: "Construction Contract",
    emoji: "🔨",
    category: "Real Estate",
    shortDesc: "Catch change-order and lien risks",
  },
  {
    slug: "contractor-agreement",
    name: "Contractor Agreement",
    emoji: "👷",
    category: "Real Estate",
    shortDesc: "Decode payment, scope, and warranty terms",
  },
  {
    slug: "offer-letter",
    name: "Job Offer Letter",
    emoji: "📝",
    category: "Employment",
    shortDesc: "Understand comp, equity, and at-will terms",
  },
  {
    slug: "severance-agreement",
    name: "Severance Agreement",
    emoji: "👋",
    category: "Employment",
    shortDesc: "Know what you're giving up for severance",
  },
  {
    slug: "non-solicit",
    name: "Non-Solicitation Agreement",
    emoji: "📵",
    category: "Employment",
    shortDesc: "Understand who you can hire or contact",
  },
  {
    slug: "stock-option-agreement",
    name: "Stock Option Agreement",
    emoji: "📈",
    category: "Employment",
    shortDesc: "Decode vesting, strike price, and cliffs",
  },
  {
    slug: "rsu-agreement",
    name: "RSU Agreement",
    emoji: "💎",
    category: "Employment",
    shortDesc: "Understand vesting and tax implications",
  },
  {
    slug: "consulting-agreement",
    name: "Consulting Agreement",
    emoji: "🧑‍💼",
    category: "Employment",
    shortDesc: "Spot scope creep and IP transfers",
  },
  {
    slug: "independent-contractor-agreement",
    name: "Independent Contractor Agreement",
    emoji: "🧑‍💻",
    category: "Employment",
    shortDesc: "Know your rights as a 1099 worker",
  },
  {
    slug: "internship-agreement",
    name: "Internship Agreement",
    emoji: "🎓",
    category: "Employment",
    shortDesc: "Catch unpaid-work and IP issues",
  },
  {
    slug: "employee-handbook",
    name: "Employee Handbook",
    emoji: "📚",
    category: "Employment",
    shortDesc: "Decode policies and at-will language",
  },
  {
    slug: "performance-improvement-plan",
    name: "Performance Improvement Plan (PIP)",
    emoji: "📊",
    category: "Employment",
    shortDesc: "Understand expectations and risks",
  },
  {
    slug: "non-disparagement",
    name: "Non-Disparagement Clause",
    emoji: "🤐",
    category: "Employment",
    shortDesc: "Know what you can and can't say",
  },
  {
    slug: "msa",
    name: "Master Service Agreement (MSA)",
    emoji: "📁",
    category: "Business",
    shortDesc: "Decode shared terms across SOWs",
  },
  {
    slug: "sow",
    name: "Statement of Work (SOW)",
    emoji: "📋",
    category: "Business",
    shortDesc: "Catch scope and milestone risk",
  },
  {
    slug: "vendor-agreement",
    name: "Vendor Agreement",
    emoji: "📦",
    category: "Business",
    shortDesc: "Spot auto-renewals and SLAs",
  },
  {
    slug: "saas-agreement",
    name: "SaaS Agreement",
    emoji: "☁️",
    category: "Business",
    shortDesc: "Understand uptime, data, and term clauses",
  },
  {
    slug: "license-agreement",
    name: "License Agreement",
    emoji: "🔑",
    category: "Business",
    shortDesc: "Know what you can and can't do",
  },
  {
    slug: "franchise-agreement",
    name: "Franchise Agreement",
    emoji: "🏪",
    category: "Business",
    shortDesc: "Decode royalties, territory, and exit terms",
  },
  {
    slug: "distribution-agreement",
    name: "Distribution Agreement",
    emoji: "🚚",
    category: "Business",
    shortDesc: "Understand exclusivity and quotas",
  },
  {
    slug: "joint-venture-agreement",
    name: "Joint Venture Agreement",
    emoji: "🧭",
    category: "Business",
    shortDesc: "Decode profit splits and control",
  },
  {
    slug: "shareholder-agreement",
    name: "Shareholder Agreement",
    emoji: "📈",
    category: "Business",
    shortDesc: "Understand voting and exit rights",
  },
  {
    slug: "operating-agreement",
    name: "LLC Operating Agreement",
    emoji: "🧱",
    category: "Business",
    shortDesc: "Decode member rights and duties",
  },
  {
    slug: "founders-agreement",
    name: "Founders Agreement",
    emoji: "🚀",
    category: "Business",
    shortDesc: "Avoid co-founder disputes early",
  },
  {
    slug: "safe-agreement",
    name: "SAFE Agreement",
    emoji: "🛟",
    category: "Business",
    shortDesc: "Understand caps, discounts, and triggers",
  },
  {
    slug: "convertible-note",
    name: "Convertible Note",
    emoji: "📄",
    category: "Business",
    shortDesc: "Decode interest, discount, and conversion",
  },
  {
    slug: "stock-purchase-agreement",
    name: "Stock Purchase Agreement",
    emoji: "📑",
    category: "Business",
    shortDesc: "Spot reps, warranties, and indemnities",
  },
  {
    slug: "asset-purchase-agreement",
    name: "Asset Purchase Agreement",
    emoji: "🏷️",
    category: "Business",
    shortDesc: "Understand what's included and excluded",
  },
  {
    slug: "bill-of-sale",
    name: "Bill of Sale",
    emoji: "🧾",
    category: "Business",
    shortDesc: "Decode warranties and as-is terms",
  },
  {
    slug: "purchase-order",
    name: "Purchase Order",
    emoji: "📦",
    category: "Business",
    shortDesc: "Catch hidden terms and conditions",
  },
  {
    slug: "supply-agreement",
    name: "Supply Agreement",
    emoji: "🏭",
    category: "Business",
    shortDesc: "Spot pricing and quantity risks",
  },
  {
    slug: "manufacturing-agreement",
    name: "Manufacturing Agreement",
    emoji: "🛠️",
    category: "Business",
    shortDesc: "Decode IP, quality, and exclusivity",
  },
];

export const documentTypes: DocType[] = [
  lease,
  nda,
  employment,
  service,
  tos,
  termSheet,
  partnership,
  nonCompete,
  ...more,
];

export const documentCategories: DocCategory[] = [
  "Personal",
  "Business",
  "Employment",
  "Real Estate",
  "Legal",
];

export function getDocBySlug(slug: string): DocType | undefined {
  return documentTypes.find((d) => d.slug === slug);
}
